import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { pageLinks } from '../../pageLinks'
import { AppShell } from '../../components/AppShell'
import { addListing } from './aplusListingsStore'
import { apiBase } from 'txom-frontend-libs'

// ============================================================================
// Page
// ============================================================================

export default function AplusAddListing() {
  const navigate = useNavigate()
  const [input, setInput] = useState('')
  const [scanning, setScanning] = useState(false)
  const [results, setResults] = useState<null | { asin: string; title: string; found: number }>(null)

  // Extract ASIN from input — supports raw ASIN or full Amazon URL
  const extractAsin = (raw: string): string | null => {
    const trimmed = raw.trim()
    // Direct ASIN: 10-char alphanumeric starting with B0
    const asinMatch = trimmed.match(/^[A-Z0-9]{10}$/i)
    if (asinMatch) return trimmed.toUpperCase()
    // Amazon URL: extract from /dp/ASIN or /gp/product/ASIN
    const urlMatch = trimmed.match(/(?:\/dp\/|\/gp\/product\/)([A-Z0-9]{10})/i)
    if (urlMatch) return urlMatch[1].toUpperCase()
    return null
  }

  const handleScan = async () => {
    if (!input.trim()) return
    const asin = extractAsin(input)
    if (!asin) {
      setResults(null)
      alert('Could not find a valid ASIN. Enter a 10-character ASIN (e.g. B0D25TMVFF) or an Amazon product URL.')
      return
    }
    setScanning(true)
    try {
      // Fetch actual product title from Amazon via backend
      const res = await fetch(apiBase + '/screenshot/lookup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ asin }),
      })
      const data = await res.json()
      setResults({
        asin,
        title: data.title || asin,
        found: 11, // Will be determined by actual screenshot captures later
      })
    } catch {
      // Fallback — still let the user add it
      setResults({
        asin,
        title: asin,
        found: 11,
      })
    } finally {
      setScanning(false)
    }
  }

  return (
    <AppShell activeNav="listings">
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-xl mx-auto px-6 py-12">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-[12px] text-[#A1A1AA] mb-6">
            <Link to={pageLinks.AplusListings()} className="hover:text-[#52525B] transition-colors">Listings</Link>
            <span>/</span>
            <span className="text-[#52525B]">Add Listing</span>
          </div>

          <div className="text-center mb-8">
            <h1 className="text-lg font-semibold text-[#18181B] mb-1">Add a Listing</h1>
            <p className="text-[13px] text-[#71717A]">Enter an ASIN or Amazon product URL to start tracking</p>
          </div>

          {/* Input */}
          <div className="bg-white rounded-lg border border-[#E5E5E5] p-6 mb-6">
            <label className="block text-[12px] font-medium text-[#18181B] mb-2">ASIN or Product URL</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="e.g. B0CX23V2ZK or https://amazon.com/dp/B0CX23V2ZK"
                className="flex-1 h-10 px-3 border border-[#E5E5E5] rounded-lg text-[13px] outline-none focus:border-[#3B82F6] transition-colors placeholder:text-[#A1A1AA]"
                onKeyDown={(e) => e.key === 'Enter' && handleScan()}
              />
              <button
                onClick={handleScan}
                disabled={!input.trim() || scanning}
                className="px-5 h-10 bg-[#18181B] text-white rounded-lg text-[13px] font-medium hover:bg-[#3F3F46] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {scanning ? 'Scanning...' : 'Scan'}
              </button>
            </div>
            <p className="text-[11px] text-[#A1A1AA] mt-2">
              We'll check all 11 marketplaces for A+ content on this listing.
            </p>
          </div>

          {/* Scanning animation */}
          {scanning && (
            <div className="bg-white rounded-lg border border-[#E5E5E5] p-8 text-center">
              <div className="size-10 mx-auto mb-3 rounded-full border-2 border-[#3B82F6] border-t-transparent animate-spin" />
              <p className="text-[13px] text-[#52525B]">Scanning marketplaces...</p>
              <p className="text-[11px] text-[#A1A1AA] mt-1">Checking 11 countries for A+ content</p>
            </div>
          )}

          {/* Results */}
          {results && !scanning && (
            <div className="bg-white rounded-lg border border-[#E5E5E5] p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="size-10 rounded-lg bg-[#F0FDF4] flex items-center justify-center">
                  <svg className="size-5 text-[#16A34A]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                  </svg>
                </div>
                <div>
                  <p className="text-[13px] font-semibold text-[#18181B]">{results.asin}</p>
                  <p className="text-[12px] text-[#71717A]">{results.title}</p>
                </div>
              </div>
              <div className="bg-[#F5F5F5] rounded-lg p-3 mb-4">
                <p className="text-[12px] text-[#52525B]">
                  Found A+ content in <span className="font-semibold text-[#16A34A]">{results.found}</span> of 11 marketplaces
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    addListing(results.asin, results.title)
                    navigate(pageLinks.AplusListingDetail(results.asin))
                  }}
                  className="flex-1 h-9 bg-[#18181B] text-white rounded-lg text-[13px] font-medium flex items-center justify-center hover:bg-[#3F3F46] transition-colors cursor-pointer"
                >
                  Add to Tracker
                </button>
                <button
                  onClick={() => { setResults(null); setInput('') }}
                  className="px-4 h-9 border border-[#E5E5E5] rounded-lg text-[13px] font-medium text-[#52525B] hover:bg-[#FAFAFA] transition-colors cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  )
}
